import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from "@mui/material";
import { useAppFeedback } from "hooks/useAppFeedback";

import {
  findCriterioAprovacaoTurma,
  saveCriterioAprovacaoTurma,
} from "API/apiAcoes";

import styles_grid from "../../../styles/Grid.module.css";
import styles_general from "../../../styles/General.module.css";
import styles_input from "../../../styles/Input.module.css";
import styles_button from "../../../styles/Button.module.css";

type TipoCriterioAprovacao =
  | "PRESENCA"
  | "ATIVIDADE_AVALIATIVA"
  | "PRESENCA_E_ATIVIDADE";

interface TurmaCriteriosFormProps {
  onSaveSuccess?: () => void;
}

const TurmaCriteriosForm = ({ onSaveSuccess }: TurmaCriteriosFormProps) => {
  const [searchParams] = useSearchParams();
  const turmaId = searchParams.get("id");
  const { showError, showSuccess } = useAppFeedback();

  const [msgError, setMsgError] = useState<string | null>(null);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [loadingCriterio, setLoadingCriterio] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);

  const [tipo, setTipo] = useState<TipoCriterioAprovacao | undefined>();
  const [porcentagemAprovacao, setPorcentagemAprovacao] = useState<number | null>(null);
  const [pontuacaoAprovado, setPontuacaoAprovado] = useState<number | null>(null);
  const [habilitarCadastro, setHabilitarCadastro] = useState(false);
  const [habilitarPresencaNaoInscrito, setHabilitarPresencaNaoInscrito] = useState(false);

  const mostraPontuacoes = useMemo(
    () => tipo === "ATIVIDADE_AVALIATIVA" || tipo === "PRESENCA_E_ATIVIDADE",
    [tipo],
  );
  const mostraPorcentagemAprovacao = useMemo(
    () => tipo === "PRESENCA" || tipo === "PRESENCA_E_ATIVIDADE",
    [tipo],
  );
  const mostraOpcoesPresenca = useMemo(
    () => tipo === "PRESENCA" || tipo === "PRESENCA_E_ATIVIDADE",
    [tipo],
  );

  useEffect(() => {
    if (!turmaId) {
      return;
    }

    const carregarCriterio = async () => {
      setLoadingCriterio(true);
      try {
        const response = await findCriterioAprovacaoTurma(turmaId);
        if (!response) {
          setLoadingCriterio(false);
          return;
        }

        setTipo(response.tipo as TipoCriterioAprovacao);
        setPorcentagemAprovacao(response.porcentagem_aprovacao ?? null);
        setPontuacaoAprovado(response.pontuacao_aprovado ?? null);
        setHabilitarCadastro(
          response.habilitar_cadastro === 1 || response.habilitar_cadastro === true,
        );
        setHabilitarPresencaNaoInscrito(
          response.habilitar_presenca_nao_inscrito === 1 ||
            response.habilitar_presenca_nao_inscrito === true,
        );
      } catch (error) {
        setMsgError("Erro ao carregar o criterio de aprovacao da turma");
      } finally {
        setLoadingCriterio(false);
      }
    };

    carregarCriterio();
  }, [turmaId]);

  useEffect(() => {
    if (msgSuccess) {
      showSuccess(msgSuccess);
      setMsgSuccess(null);
    }
  }, [msgSuccess, showSuccess]);

  useEffect(() => {
    if (msgError) {
      showError(msgError);
      setMsgError(null);
    }
  }, [msgError, showError]);

  const handlePontuacaoChange =
    (setter: (value: number | null) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setter(value === "" ? null : parseInt(value, 10));
    };

  const handlePorcentagemChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPorcentagemAprovacao(value === "" ? null : parseFloat(value));
  };

  const validateForm = () => {
    if (!tipo) {
      setMsgError("Selecione um criterio de aprovacao");
      return false;
    }

    if (mostraPorcentagemAprovacao && porcentagemAprovacao === null) {
      setMsgError("Preencha a porcentagem de aprovacao");
      return false;
    }

    if (
      mostraPorcentagemAprovacao &&
      porcentagemAprovacao !== null &&
      (porcentagemAprovacao < 0 || porcentagemAprovacao > 100)
    ) {
      setMsgError("A porcentagem de aprovacao deve estar entre 0 e 100");
      return false;
    }

    if (mostraPontuacoes && pontuacaoAprovado === null) {
      setMsgError("Preencha a pontuacao de aprovacao");
      return false;
    }

    return true;
  };

  const saveCriterio = async () => {
    if (!turmaId) {
      setMsgError("Nao foi possivel encontrar o codigo da turma");
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoadingSave(true);

      const opcoesPresenca = {
        habilitar_cadastro: habilitarCadastro ? 1 : 0,
        habilitar_presenca_nao_inscrito: habilitarPresencaNaoInscrito ? 1 : 0,
      };

      const payload =
        tipo === "PRESENCA"
          ? {
              tipo,
              porcentagem_aprovacao: porcentagemAprovacao,
              ...opcoesPresenca,
            }
          : tipo === "ATIVIDADE_AVALIATIVA"
            ? {
                tipo,
                pontuacao_aprovado: pontuacaoAprovado,
              }
            : {
                tipo,
                porcentagem_aprovacao: porcentagemAprovacao,
                pontuacao_aprovado: pontuacaoAprovado,
                ...opcoesPresenca,
              };

      const response = await saveCriterioAprovacaoTurma(turmaId, payload);

      if (!response?.success) {
        setMsgError("Nao foi possivel salvar o criterio de aprovacao da turma");
        return;
      }

      setMsgSuccess("Criterio de aprovacao salvo com sucesso");
      onSaveSuccess?.();
    } catch (error) {
      setMsgError("Erro ao salvar o criterio de aprovacao da turma");
    } finally {
      setLoadingSave(false);
    }
  };

  if (loadingCriterio) {
    return <CircularProgress />;
  }

  return (
    <div className={`${styles_general.mt2} ${styles_general.p2} ${styles_grid.grid12}`}>
      <div className={`${styles_grid.inputGroupFull} ${styles_grid.justifyStart}`}>
        <p className={styles_general.titlePrimary}>Selecione um criterio de aprovacao</p>
      </div>

      <div
        className={`${styles_grid.inputGroupFullNotMargin} ${styles_grid.justifyStart} ${styles_general.mt2}`}
      >
        <FormControl>
          <RadioGroup
            aria-labelledby="criterio-aprovacao"
            name="criterio-aprovacao"
            value={tipo ?? ""}
            onChange={(_event, newValue) => {
              if (!newValue) {
                return;
              }
              setTipo(newValue as TipoCriterioAprovacao);
              if (newValue === "PRESENCA") {
                setPontuacaoAprovado(null);
              }
              if (newValue === "ATIVIDADE_AVALIATIVA") {
                setPorcentagemAprovacao(null);
              }
            }}
          >
            <FormControlLabel
              value="PRESENCA"
              control={<Radio size="small" />}
              label={
                <span className={styles_input.labelRadio}>
                  <strong>Presenca</strong> (via link QRCode, lancamento manual ou upload de lista csv)
                </span>
              }
            />
            <FormControlLabel
              value="ATIVIDADE_AVALIATIVA"
              control={<Radio size="small" />}
              label={
                <span className={styles_input.labelRadio}>
                  <strong>Atividade avaliativa</strong>
                </span>
              }
            />
            <FormControlLabel
              value="PRESENCA_E_ATIVIDADE"
              control={<Radio size="small" />}
              label={
                <span className={styles_input.labelRadio}>
                  <strong>Presenca</strong> (via link QRCode, lancamento manual ou upload de lista csv) +{" "}
                  <strong>Atividade avaliativa</strong>
                </span>
              }
            />
          </RadioGroup>
        </FormControl>
      </div>

      {mostraPorcentagemAprovacao && (
        <div className={styles_grid.inputGroupFull}>
          <div className={`${styles_grid.justifyStart} ${styles_grid.itemsCenter} ${styles_general.gap2}`}>
            <p className={styles_general.titlePrimary}>Porcentagem de aprovacao:</p>
            <div className="w-20">
              <TextField
                type="number"
                size="small"
                placeholder="%"
                variant="standard"
                fullWidth
                data-testid="porcentagem-aprovacao"
                value={porcentagemAprovacao ?? ""}
                onChange={handlePorcentagemChange}
                required
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                InputLabelProps={{ shrink: true }}
                InputProps={{ className: styles_input.input, disableUnderline: true }}
              />
            </div>
            <p className={styles_general.titlePrimary}>%</p>
          </div>
        </div>
      )}

      {mostraPontuacoes && (
        <div className={styles_grid.inputGroupFull}>
          <div className={`${styles_grid.justifyStart} ${styles_grid.itemsCenter} ${styles_general.gap2}`}>
            <p className={styles_general.titlePrimary}>Aprovado &ge;</p>
            <div className="w-16">
              <TextField
                type="number"
                size="small"
                placeholder=">="
                variant="standard"
                fullWidth
                data-testid="pontuacao-aprovado"
                value={pontuacaoAprovado ?? ""}
                onChange={handlePontuacaoChange(setPontuacaoAprovado)}
                required
                InputLabelProps={{ shrink: true }}
                InputProps={{ className: styles_input.input, disableUnderline: true }}
              />
            </div>
          </div>
        </div>
      )}

      {mostraOpcoesPresenca && (
        <div className={styles_grid.inputGroupFull}>
          <div className={`${styles_grid.justifyStart} ${styles_grid.itemsCenter}`}>
          <FormControlLabel
            control={
              <Switch
                checked={habilitarCadastro}
                onChange={(_event, checked) => setHabilitarCadastro(checked)}
                color="primary"
              />
            }
            label={
              <span className={styles_general.titlePrimary}>
                Habilitar cadastro de novos usuários
              </span>
            }
          />
          </div>
          <div className={`${styles_grid.justifyStart} ${styles_grid.itemsCenter}`}>
            <FormControlLabel
              control={
                <Switch
                  checked={habilitarPresencaNaoInscrito}
                  onChange={(_event, checked) => setHabilitarPresencaNaoInscrito(checked)}
                  color="primary"
                />
              }
              label={
                <span className={styles_general.titlePrimary}>
                  Habilitar presença para não inscritos
                </span>
              }
            />
          </div>
        </div>
      )}

      <div className={`${styles_grid.inputGroupFull} ${styles_grid.justifyCenter} ${styles_general.mt2}`}>
        <Button
          variant="contained"
          onClick={saveCriterio}
          className={`${styles_button.buttonPrimary} ${loadingSave ? styles_button.buttonLoad : ""} `}
          color="primary"
        >
          {loadingSave ? (
            <>
              <div className={styles_button.spinner}></div>
              <span>Salvando...</span>
            </>
          ) : (
            <span>Salvar Criterio de Aprovacao</span>
          )}
        </Button>
      </div>
    </div>
  );
};

export default TurmaCriteriosForm;
